import { createConfig } from '@privy-io/wagmi';
import { fallback, http } from 'viem';
import type { Transport } from 'viem';
import {
  arbitrum,
  avalanche,
  base,
  bsc,
  mainnet,
  optimism,
  polygon,
  scroll,
  linea,
  mantle,
  mode,
  unichain,
  plasma,
  abstract,
  monad,
  ink,
} from 'viem/chains';

import { ClientConfigurationError } from '@/lib/errors/client';

import { hyperEvm, robinhoodChain, SUPPORTED_CHAINS, SupportedChainIDs } from '@/constants/chain';
import {
  JWT_PROTECTED_JSON_RPC_HTTP_URL_BY_CHAIN_ID,
  JWT_PROTECTED_JSON_RPC_TRACE_HTTP_URL_BY_CHAIN_ID,
} from '@/constants/rpc';

import { ENV } from '@/environment/client';

import { getNodeProviderJwt } from '@/hooks/useNodeProviderToken';

if (!ENV.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID) {
  throw new ClientConfigurationError('A WalletConnect project ID is required.');
}

export const config = createConfig({
  chains: [
    mainnet,
    polygon,
    bsc,
    arbitrum,
    optimism,
    avalanche,
    base,
    scroll,
    linea,
    mantle,
    mode,
    unichain,
    plasma,
    monad,
    abstract,
    hyperEvm,
    robinhoodChain,
    ink,
  ],
  transports: getTransports(),
  ssr: true,
});

/**
 * Creates a mapping of chain IDs to transports for supported Ethereum chains.
 * Chains with a dedicated trace URL use a fallback transport to route debug tracing separately.
 *
 * The function sets up an `onFetchRequest` handler for each transport, which manages JWT authentication dynamically.
 *
 * Some notes about the JWT handling within the `http` transport:
 * - Retrieves the JWT from the browser's `localStorage`.
 * - The JWT is stored in `localStorage` because it refreshes frequently (e.g., every 5 minutes) and needs to be accessed dynamically.
 * - Dynamically reading the JWT from `localStorage` within the `onFetchRequest` handler prevents creating a new Wagmi configuration for each request, which would disconnect the user.
 * - Adds the JWT as a `Bearer` token in the `Authorization` header for each HTTP request when the `jwt-node-provider` flag is present in the URL.
 * - Falls back to the default RPC URL if the JWT is invalid, missing, or expired.
 * - If the `jwt-node-provider` flag is not present, it proceeds without adding the JWT.
 */
function getTransports() {
  return SUPPORTED_CHAINS.reduce<Record<SupportedChainIDs, Transport>>(
    (transportsByChainId, chain) => {
      const chainId = chain.id;
      const rpcUrl = JWT_PROTECTED_JSON_RPC_HTTP_URL_BY_CHAIN_ID[chainId];
      const traceRpcUrl = JWT_PROTECTED_JSON_RPC_TRACE_HTTP_URL_BY_CHAIN_ID[chainId];
      const onFetchRequest = (_: Request, init: RequestInit) => {
        try {
          return {
            ...init,
            headers: { ...init.headers, Authorization: `Bearer ${getNodeProviderJwt()}` },
          };
        } catch {
          return { ...init, url: chain.rpcUrls.default.http[0] };
        }
      };

      const defaultTransport = http(rpcUrl, {
        methods: traceRpcUrl ? { exclude: ['debug_traceTransaction'] } : undefined,
        onFetchRequest,
      });
      transportsByChainId[chainId] = traceRpcUrl
        ? fallback([
            http(traceRpcUrl, {
              methods: { include: ['debug_traceTransaction'] },
              onFetchRequest,
            }),
            defaultTransport,
          ])
        : defaultTransport;

      return transportsByChainId;
    },
    {},
  );
}
